import React, { useEffect, useState } from 'react';
import Button from '../../ui/Button';
import { Field, Notice, inputStyle, apiErrors } from './formBits';

// Ordered ICD-10 list for one client (rank 1 = primary). Codes are normalized by
// the server; this editor only arranges rows and sends { code, description }[].
function normalizeForDisplay(code) {
  const s = String(code || '').trim().toUpperCase().replace(/\./g, '');
  return s.length > 3 ? `${s.slice(0, 3)}.${s.slice(3)}` : s;
}

export default function DiagnosisEditor({ diagnoses, canEdit, onSave }) {
  const [rows, setRows] = useState([]);
  const [code, setCode] = useState('');
  const [desc, setDesc] = useState('');
  const [errors, setErrors] = useState({});
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Follow the parent's list only while there are no unsaved edits; a refetch
  // caused by a card upload or policy save must not throw away a half-done
  // reorder. Once a save settles (dirty → false) the editor re-syncs.
  useEffect(() => {
    if (dirty) return;
    setRows((diagnoses || []).map((d) => ({ code: d.code, description: d.description || '' })));
  }, [diagnoses, dirty]);

  const markDirty = (v) => setDirty(v);
  const update = (next) => { setRows(next); markDirty(true); setErrors({}); setMsg(null); };
  const add = () => {
    const c = code.trim();
    if (!c) return;
    update([...rows, { code: c.toUpperCase().replace(/\./g, ''), description: desc.trim() }]);
    setCode(''); setDesc('');
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const next = rows.slice(); [next[i], next[j]] = [next[j], next[i]]; update(next);
  };
  const remove = (i) => update(rows.filter((_, k) => k !== i));

  async function save() {
    setSaving(true); setMsg(null);
    try {
      await onSave(rows.map((r) => ({ code: r.code, description: r.description || null })));
      setErrors({}); markDirty(false); setMsg({ tone: 'good', text: 'Diagnoses saved.' });
    } catch (err) {
      const { fieldErrors, message } = apiErrors(err);
      setErrors(fieldErrors); setMsg({ tone: 'bad', text: message });
    } finally { setSaving(false); }
  }

  return (
    <div>
      {rows.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)', margin: '4px 0 8px' }}>No diagnoses on file.</div>}
      <ol style={{ margin: 0, paddingLeft: 22 }}>
        {rows.map((r, i) => (
          <li key={`${r.code}-${i}`} style={{ margin: '4px 0', fontSize: 14 }}>
            <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{normalizeForDisplay(r.code)}</span>
            {r.description ? <span style={{ color: 'var(--text-strong)' }}> — {r.description}</span> : null}
            {i === 0 && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)' }}>(primary)</span>}
            {canEdit && (
              <span style={{ marginLeft: 10, whiteSpace: 'nowrap' }}>
                <button type="button" aria-label="Move up" onClick={() => move(i, -1)} disabled={i === 0} style={iconBtn}>▲</button>
                <button type="button" aria-label="Move down" onClick={() => move(i, 1)} disabled={i === rows.length - 1} style={iconBtn}>▼</button>
                <button type="button" aria-label="Remove" onClick={() => remove(i)} style={{ ...iconBtn, color: 'var(--error-color)' }}>✕</button>
              </span>
            )}
            {errors[`diagnoses.${i}`] && <div role="alert" style={{ fontSize: 12, color: 'var(--error-color)' }}>{errors[`diagnoses.${i}`]}</div>}
          </li>
        ))}
      </ol>
      {errors.diagnoses && <div role="alert" style={{ fontSize: 12, color: 'var(--error-color)', marginTop: 6 }}>{errors.diagnoses}</div>}
      {canEdit && (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 8 }}>
            <Field label="ICD-10 code" style={{ maxWidth: 140 }}><input style={inputStyle} value={code} onChange={(e) => setCode(e.target.value)} placeholder="F41.1" aria-label="ICD-10 code" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} /></Field>
            <Field label="Description" style={{ maxWidth: 360 }}><input style={inputStyle} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Generalized anxiety disorder" aria-label="Description" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} /></Field>
            <Button type="button" variant="subtle" onClick={add} disabled={!code.trim() || rows.length >= 12} style={{ marginBottom: 2 }}>Add</Button>
          </div>
          {rows.length >= 12 && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Claims carry at most 12 diagnoses.</div>}
          {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
          <div style={{ marginTop: 8 }}>
            <Button type="button" onClick={save} disabled={saving || !dirty}>{saving ? 'Saving…' : 'Save diagnoses'}</Button>
          </div>
        </>
      )}
    </div>
  );
}

const iconBtn = { background: 'transparent', border: '1px solid var(--border-color)', borderRadius: 4, padding: '0 6px', marginLeft: 3, cursor: 'pointer', fontSize: 11, color: 'var(--text-strong)' };
